<template>
  <v-card variant="outlined" class="workshop-summary">
    <div class="d-flex flex-wrap align-center ga-2 pa-3">
      <span class="text-body-2 font-weight-medium mr-auto">
        {{
          $t("workshop.summary.header", {
            done: summary.done,
            total: summary.total,
          })
        }}
        <template v-if="summary.optional">
          ·
          {{ $t("workshop.summary.optional", { count: summary.optional }) }}
        </template>
      </span>
      <span class="text-caption text-medium-emphasis">
        {{
          $t(
            $vuetify.display.mdAndUp
              ? "workshop.summary.select_hint"
              : "workshop.summary.tap_hint",
          )
        }}
      </span>
    </div>
    <v-divider />
    <v-list density="compact" class="py-0">
      <template v-for="group in summary.groups" :key="group.group">
        <v-list-subheader>{{ $t(group.groupKey) }}</v-list-subheader>
        <v-list-item
          v-for="row in group.rows"
          :key="row.code"
          min-height="48"
          :active="row.code === selected"
          color="primary"
          @click="$emit('open', row.code)"
        >
          <v-list-item-title class="workshop-wrap text-wrap">
            {{ $t(taskKey(row.code)) }}
            <span
              v-if="!row.mandatory"
              class="text-caption text-medium-emphasis"
            >
              {{ $t("workshop.checklist.optional") }}
            </span>
          </v-list-item-title>
          <v-list-item-subtitle
            v-if="row.quantity || row.reason || row.evidence"
            class="workshop-wrap text-wrap"
          >
            <template v-if="row.quantity">{{
              quantity(row.quantity)
            }}</template>
            <template v-else-if="row.reason">{{ row.reason }}</template>
            <template v-if="row.evidence">
              <template v-if="row.quantity || row.reason"> · </template>
              <v-icon size="14">mdi-paperclip</v-icon>
              {{ $t("workshop.summary.evidence", { count: row.evidence }) }}
            </template>
          </v-list-item-subtitle>
          <template #append>
            <v-chip size="x-small" variant="tonal" :color="row.status.color">
              {{ $t(row.status.key) }}
            </v-chip>
          </template>
        </v-list-item>
      </template>
    </v-list>
  </v-card>
</template>

<script>
import {
  approvedSummary,
  taskKey,
} from "@/components/Workshop/workshopView.mjs";

// The summary list of an approved checklist (Stage 3a plan §D2, slice 8), the same at both widths:
// "x di y obbligatorie · n facoltativa", then one row per task in template order, grouped by group:
// label, result chip, quantity (value, unit, basis) or reason, evidence count. The whole row is the
// tap target (at least 48 px) and opens the read-only detail (desktop: in the column beside it,
// with the row highlighted).
export default {
  name: "WorkshopApprovedSummary",
  props: {
    // The checklist view (approved)
    view: { type: Object, required: true },
    // Desktop: the task shown in the detail column, highlighted
    selected: { type: String, default: null },
  },
  emits: ["open"],
  computed: {
    summary() {
      return approvedSummary(this.view);
    },
  },
  methods: {
    taskKey,
    quantity(value) {
      const number = new Intl.NumberFormat(this.$i18n.locale, {
        maximumFractionDigits: 3,
      }).format(value.value);
      const basis = value.basis
        ? ` · ${this.$t(`workshop.basis.${value.basis}`)}`
        : "";
      return `${number} ${value.unit || ""}${basis}`;
    },
  },
};
</script>

<style scoped>
.workshop-wrap {
  overflow-wrap: anywhere;
}
</style>
